import * as jobService from '../services/jobDashboardService';
import { isDriverOnlyUser, trackAssignedJob } from './jobUtils';
import { canChangeJobStatus, getUserPermissions } from './authUtils';
// Import useUser to get currentUser

/**
 * Handlers for job actions (accept, reject, dispatch, etc.)
 */

// Helper to check if user can act as SP for a job offer
const canActAsSP = (user) => {
  if (!user) return false;
  return user.primaryRole === 'SP' ||
         user.secondaryRoles?.admin === true ||
         user.secondaryRoles?.dispatcher === true ||
         user.secondaryRoles?.answeringService === true;
};


// Accept job handler - Modified to differentiate SP vs Driver
export const handleAcceptJob = (job, setSelectedJob, setEtaDialogOpen, handleDirectAccept, handleDirectSPAccept, currentUser) => { // Pass currentUser
  setSelectedJob(job);
  const isSPAction = job.status === 'Searching SP' && canActAsSP(currentUser); // Use currentUser

  if (isSPAction) {
    // SP accepting a "Searching SP" job
    // ETA is always required for SP acceptance according to backend logic
    setEtaDialogOpen(true);
    // Note: handleDirectSPAccept is not called here because ETA is mandatory for SPs
  } else {
    // Driver accepting a "Pending Acceptance" job
    const etaIsValidNumeric = job.eta && /^\d+$/.test(job.eta.toString().trim());
    if (etaIsValidNumeric) {
      // If job already has a valid numeric ETA, accept it directly
      handleDirectAccept(job);
    } else {
      // If ETA is not valid numeric (e.g., "ASAP", "Scheduled...", or empty/null), show dialog
      setEtaDialogOpen(true);
    }
  }
};

// Direct accept for DRIVER without showing ETA dialog
export const handleDirectAccept = async (job, startLoading, refreshJobs, setNotification) => {
  const stopLoading = startLoading();
  
  try {
    // Accept the job with the existing ETA or default to 30 minutes
    await jobService.directAcceptJob(job.id, job.eta || "30");
    
    // Also track this in localStorage for persistence
    const currentUser = JSON.parse(localStorage.getItem('userInfo') || '{}');
    if (currentUser && currentUser.id) {
      trackAssignedJob(job.id, currentUser.id);
    }
    
    // Refresh the jobs list
    refreshJobs();
    
  } catch (error) {
    console.error('Error accepting job:', error);
    const status = error.response?.status;
    const serverMessage = error.response?.data?.message;
    // Check if setNotification is provided before using it
    if (setNotification) {
      if (status === 409) {
        // Blast dispatch: someone else accepted first
        setNotification({ open: true, message: serverMessage || 'Another provider has already accepted this job.', severity: 'warning' });
        refreshJobs();
      } else {
        setNotification({ open: true, message: serverMessage || 'Failed to accept job. Please try again.', severity: 'error' });
      }
    } else {
      // Fallback to console error if setNotification is not provided
      console.error('Failed to accept job. Please try again.');
    }
  } finally {
    stopLoading(); // Use stopLoading
  }
};

// NEW: Direct accept for SP (if ETA already exists - though current flow requires dialog)
// Kept for potential future use or edge cases
export const handleDirectSPAccept = async (job, startLoading, refreshJobs, setNotification) => {
    const stopLoading = startLoading();
    try {
        // SP acceptance requires ETA, use existing job.eta
        if (!job.eta) {
            if (setNotification) {
                setNotification({ open: true, message: 'ETA is missing. Cannot directly accept SP job offer.', severity: 'error' });
            } else {
                console.error('ETA is missing. Cannot directly accept SP job offer.');
            }
            throw new Error('ETA is missing for SP acceptance.');
        }
        await jobService.acceptJobBySP(job.id, job.eta); // Call the SP accept endpoint
        refreshJobs();
    } catch (error) {
        console.error('Error directly accepting job by SP:', error);
        if (setNotification) {
            setNotification({ open: true, message: 'Failed to accept job offer. Please try again.', severity: 'error' });
        } else {
            console.error('Failed to accept job offer. Please try again.');
        }
    } finally {
        stopLoading();
    }
};


// Save ETA selection and accept job - MODIFIED to handle SP vs Driver
export const handleEtaDialogSave = async (etaValue, selectedJob, startLoading, refreshJobs, setEtaDialogOpen, setSelectedJob, currentUser, setNotification) => {
  const stopLoading = startLoading();

  try {
    // Determine if this is an SP accepting a 'Searching SP' job
    const isSPAction = selectedJob.status === 'Searching SP' && canActAsSP(currentUser); // Use passed currentUser

    if (isSPAction) {
      // SP Acceptance Flow
      await jobService.acceptJobBySP(selectedJob.id, etaValue);
      // No local storage tracking needed here as it's an org assignment
      // console.log(`SP Org ${currentUser.vendorNumber || currentUser.vendorId} accepted job ${selectedJob.id}`); // Removed log
    } else {
      // Driver Acceptance Flow
      await jobService.acceptJob(selectedJob.id, etaValue);
      // Track driver assignment in localStorage
      if (currentUser && currentUser.id) {
        trackAssignedJob(selectedJob.id, currentUser.id);
      }
      // Driver acceptance requires tracking in local storage
      if (currentUser && currentUser.id) {
        trackAssignedJob(selectedJob.id, currentUser.id);
      }
      // console.log(`Driver ${currentUser?.id} accepted job ${selectedJob.id}`); // Use optional chaining // Removed log
    }

    // Refresh jobs to get the updated data
    refreshJobs();

    // Close the dialog
    setEtaDialogOpen(false);
    setSelectedJob(null);
  } catch (error) {
    const status = error.response?.status;
    const serverMessage = error.response?.data?.message;
    if (status === 409) {
      // Blast dispatch: someone else accepted first — tell the user plainly and
      // clear the stale offer from their dashboard.
      setNotification({ open: true, message: serverMessage || 'Another provider has already accepted this job.', severity: 'warning' });
      setEtaDialogOpen(false);
      setSelectedJob(null);
      refreshJobs();
    } else {
      setNotification({ open: true, message: serverMessage || 'Failed to update job. Please try again.', severity: 'error' }); // Use Snackbar
    }
  } finally {
    stopLoading();
  }
};

// Reject job handler - MODIFIED (Logic moved mostly to handleSubmitRejection)
export const handleRejectJob = (jobId, jobs, setJobToReject, setRejectDialogOpen) => {
  // Find the job to reject
  const job = jobs.find(j => j.id === jobId);
  if (!job) {
    // console.error('Job not found for rejection'); // Removed log
    return;
  }
  
  // Set the job to reject and open the rejection dialog
  setJobToReject(job);
  setRejectDialogOpen(true);
};

// Submit job rejection with reason
export const handleSubmitRejection = async (jobToReject, rejectionReason, startLoading, refreshJobs, setRejectDialogOpen, setJobToReject, setNotification) => {
  if (!jobToReject) {
    // console.error('No job selected for rejection'); // Removed log
    setNotification({ open: true, message: 'Error: No job selected for rejection', severity: 'error' });
    setRejectDialogOpen(false);
    return;
  }

  // Store the job ID in a local variable
  const jobId = jobToReject.id;
  const jobStatus = jobToReject.status; // Get status before clearing

  const stopLoading = startLoading();
  let rejectionSuccessful = false; // Flag to track API call success

  try {
    // Get current user info directly (assuming it's available via context or props eventually, using localStorage as fallback)
    // Ideally, currentUser should be passed into this function if possible.
    const currentUserInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
    const isSPAction = jobStatus === 'Searching SP' && canActAsSP(currentUserInfo); // Check if the *action* is SP-related based on job status and user

    if (isSPAction) {
       // SP Rejection Flow
       await jobService.rejectJobBySP(jobId, rejectionReason);
       // console.log(`SP Org ${currentUserInfo.vendorNumber || currentUserInfo.vendorId} rejected job ${jobId}`); // Removed log
    } else {
       // Driver Rejection Flow
       await jobService.rejectJob(jobId, rejectionReason);
       // console.log(`Driver ${currentUserInfo.id} rejected job ${jobId}`); // Removed log
    }

    rejectionSuccessful = true; // Mark as successful if API call didn't throw

  } catch (error) {
    // console.error('Error rejecting job:', error); // Removed log
    setNotification({ open: true, message: 'Failed to reject job. Please try again.', severity: 'error' }); // Use Snackbar
    // rejectionSuccessful remains false
  } finally {
    stopLoading();
    // Close dialog and refresh ONLY if the API call was successful
    if (rejectionSuccessful) {
        setRejectDialogOpen(false);
        setJobToReject(null);
        try {
            await refreshJobs(); // Refresh jobs after success
        } catch (refreshError) {
            console.error("Error refreshing jobs after successful rejection:", refreshError);
            // Optionally notify about refresh error, but rejection itself was successful
            setNotification({ open: true, message: 'Rejection successful, but failed to refresh job list.', severity: 'warning' });
        }
    }
  }
};

// Dispatch job handler
export const handleDispatchJob = (job, setSelectedJob, setDispatchDialogOpen) => {
  setSelectedJob(job);
  setDispatchDialogOpen(true);
};

// Assign driver and truck to job
export const handleAssignDriverAndTruck = async (driver, truck, selectedJob, startLoading, refreshJobs, setDispatchDialogOpen, setSelectedJob, vehicles = [], setNotification) => {
  const stopLoading = startLoading(); // Use startLoading
  
  try {
    // Check if this is a redispatch (job already has assignedAt and/or firstAssignedAt)
    const isRedispatch = selectedJob.assignedAt || selectedJob.firstAssignedAt;
    
    // If this is a redispatch, preserve the original assignedAt time
    // by explicitly sending it back to the server
    const preserveAssignedAt = isRedispatch;
    const originalAssignedAt = selectedJob.firstAssignedAt || selectedJob.assignedAt;
    
    // Check if truck is an ObjectId (24 hex characters) and convert to a readable name if possible
    let truckName = truck;
    
    // Check if truck looks like an ObjectId (24 hex characters)
    if (typeof truck === 'string' && /^[0-9a-f]{24}$/i.test(truck)) {
      // Find the vehicle in the vehicles array
      const vehicle = vehicles.find(v => v._id === truck);
      
      if (vehicle) {
        // Construct a readable name from the vehicle properties
        if (vehicle.name) {
          truckName = vehicle.name;
        } else if (vehicle.year && vehicle.make && vehicle.model) {
          truckName = `${vehicle.year} ${vehicle.make} ${vehicle.model}`;
        } else if (vehicle.make && vehicle.model) {
          truckName = `${vehicle.make} ${vehicle.model}`;
        } else if (vehicle.identifier) {
          truckName = vehicle.identifier;
        }
        // If none of the above, keep the original truck value
      }
    }
    
    // console.log(isRedispatch ? 'Redispatching job' : 'First-time dispatch for job:', { // Removed log
    //   jobId: selectedJob.id, // Removed log
    //   driverId: driver.id, // Removed log
    //   driverName: driver.name, // Removed log
    //   originalTruck: truck, // Removed log
    //   truckName, // Removed log
    //   preserveAssignedAt, // Removed log
    //   originalAssignedAt, // Removed log
    //   isRedispatch // Removed log
    // }); // Removed log
    
    // Use the jobService to assign the driver and truck (using the readable name)
    await jobService.assignDriverAndTruck(selectedJob.id, driver.id, truckName, preserveAssignedAt, originalAssignedAt);
    
    // Track this assignment in localStorage for persistence across refreshes
    trackAssignedJob(selectedJob.id, driver.id);
    
    // Refresh jobs and close dialog
    refreshJobs();
    setDispatchDialogOpen(false);
    setSelectedJob(null);
    
  } catch (error) {
    // Extract the specific error message from the response if available
    const errorMessage = error.response?.data?.message || 'Failed to assign driver and truck. Please try again.';
    console.error('Error assigning driver and truck:', errorMessage);
    setNotification({ open: true, message: errorMessage, severity: 'error' });
  } finally {
    stopLoading(); // Use stopLoading
  }
};

// Job status change handler
export const handleJobStatusChange = async (
  jobId, 
  status, 
  jobs, 
  currentUser, 
  startLoading, // Changed setLoading to startLoading
  refreshJobs, 
  handleDispatchJob, 
  setJobs, 
  jobCategory,
  getStatusPriority,
  setNotification // Add setNotification
) => {
  const stopLoading = startLoading(); // Use startLoading
  // console.log('handleJobStatusChange called with:', { jobId, status }); // Removed log
  // Removed setLoading(true)

  try {
    // Get the current job details
    const currentJob = jobs.find(job => job.id === jobId);
    if (!currentJob) {
      // console.error('Job not found for status change'); // Removed log
      setNotification({ open: true, message: 'Failed to update job status. Job not found.', severity: 'error' }); // Use Snackbar
      stopLoading(); // Use stopLoading
      return;
    }
    const oldStatus = currentJob.status;
    
    // console.log('Current job details:', { // Removed log
    //   id: currentJob.id, // Removed log
    //   oldStatus, // Removed log
    //   newStatus: status, // Removed log
    //   jobCategory // Removed log
    // }); // Removed log

    // Log currentUser for debugging
    // console.log('currentUser in handleJobStatusChange:', currentUser); // Removed log
    
    // Check if the user is a driver-only user
    const isDriverOnly = isDriverOnlyUser(currentUser);
    
    // Check if the user is a N/A role user
    const isNARole = currentUser?.primaryRole === 'N/A';
    
    // Check if status change is allowed using only the permission system
    const isStatusChangeAllowed = canChangeJobStatus(oldStatus, status);
    
    // If the status change is not allowed, display an error and return
    if (!isStatusChangeAllowed) {
      // Get permissions for detailed logging
      const permissions = getUserPermissions();
      // console.log('User permissions for job status change:', permissions); // Removed log
      
      // Get allowed progressions from permissions
      const allowedProgressions = permissions?.allowedJobProgressions || {};
      const allowedNextStatuses = allowedProgressions[oldStatus] || [];
      
      // console.log(`Status change rejected: ${oldStatus} -> ${status}`); // Removed log
      // console.log(`Allowed next statuses from permission system: ${allowedNextStatuses.join(', ')}`); // Removed log
      
      // Use Snackbar for permission errors
      if (allowedNextStatuses.length > 0) {
        setNotification({ open: true, message: `You can only change from ${oldStatus} to ${allowedNextStatuses.join(' or ')}.`, severity: 'warning' });
      } else if (jobCategory === 'completed' || jobCategory === 'canceled') {
        setNotification({ open: true, message: 'You cannot change the status of completed or canceled jobs.', severity: 'warning' });
      } else {
        setNotification({ open: true, message: `You do not have permission to change this job's status from ${oldStatus} to ${status}.`, severity: 'error' });
      }
      
      stopLoading(); // Use stopLoading
      return;
    }

    // Check if this is a previously rejected job being dispatched
    if (oldStatus === 'Pending' && currentJob.rejectionReason && status === 'Dispatched') {
      // console.log('Detected rejected job being dispatched, opening dispatch dialog'); // Removed log
      // Open dispatch dialog to assign a new driver
      handleDispatchJob(currentJob);
      return; // Exit early to avoid double updates
    }

    // console.log('Updating job status on server:', { jobId, status }); // Removed log
    
    // Use the jobService to update the job status
    await jobService.updateJobStatus(jobId, status);

    // Update local state
    setJobs(prevJobs => 
      prevJobs.map(job => job.id === jobId ? { ...job, status } : job)
    );
    
    // console.log('Local state updated with new status'); // Removed log

    // Re-prompt for driver if status changed back to Dispatched from a higher status
    if (status === 'Dispatched' && getStatusPriority(oldStatus) > getStatusPriority('Dispatched')) {
      // console.log('Status changed to Dispatched from higher status, opening dispatch dialog'); // Removed log
      handleDispatchJob(currentJob); // Re-open dispatch dialog
    }
  } catch (error) {
    // console.error('Error updating job status:', error); // Removed log
    // console.error('Error details:', error.response?.data || error.message); // Removed log
    setNotification({ open: true, message: 'Failed to update job status. Please try again.', severity: 'error' }); // Use Snackbar
  } finally {
    stopLoading(); // Use stopLoading
  }
};

// Expand/collapse job details
export const handleExpandClick = (jobId, setJobs) => {
  // console.log(`Expand button clicked for job ${jobId}`); // Removed log
  
  setJobs((prevJobs) => {
    // Find the job to verify its current expanded state
    const targetJob = prevJobs.find(job => job.id === jobId);
    const currentExpandedState = targetJob?.expanded || false;
    
    // console.log(`Current expanded state for job ${jobId}: ${currentExpandedState}`); // Removed log
    // console.log(`Setting expanded state to: ${!currentExpandedState}`); // Removed log
    
    const updatedJobs = prevJobs.map((job) =>
      job.id === jobId ? { ...job, expanded: !job.expanded } : job
    );
    
    // Verify state after update
    const updatedJob = updatedJobs.find(job => job.id === jobId);
    // console.log(`Updated expanded state for job ${jobId}: ${updatedJob?.expanded}`); // Removed log
    
    return updatedJobs;
  });
};

// Handle cancel job
export const handleCancelJob = async (jobToCancel, cancellationReason, startLoading, refreshJobs, setCancelDialogOpen, setJobToCancel, setNotification) => {
  if (!jobToCancel) {
    // console.error('No job selected for cancellation'); // Removed log
    setNotification({ open: true, message: 'Error: No job selected for cancellation', severity: 'error' });
    setCancelDialogOpen(false);
    return;
  }

  // Store the job ID in a local variable
  const jobId = jobToCancel.id;
  
  const stopLoading = startLoading(); // Use startLoading
  
  try {
    // Use the jobService to cancel the job
    await jobService.cancelJob(jobId, cancellationReason);
    
    // Close dialog and refresh jobs
    setCancelDialogOpen(false);
    setJobToCancel(null);
    refreshJobs();
    setNotification({ open: true, message: 'Job cancelled successfully.', severity: 'success' }); // Use Snackbar
  } catch (error) {
    // console.error('Error canceling job:', error); // Removed log
    setNotification({ open: true, message: 'Failed to cancel job. Please try again.', severity: 'error' }); // Use Snackbar
  } finally {
    stopLoading(); // Use stopLoading
  }
};

// Handle GOA request
export const handleGoaRequest = async (jobToMarkGOA, goaReason, startLoading, refreshJobs, setGoaDialogOpen, setJobToMarkGOA, currentUser, setNotification) => {
  if (!jobToMarkGOA) {
    // console.error('No job selected for GOA'); // Removed log
    setNotification({ open: true, message: 'Error: No job selected for GOA', severity: 'error' });
    setGoaDialogOpen(false);
    return;
  }

  // Check if the user is a driver-only user
  if (isDriverOnlyUser(currentUser)) {
    // console.log('Driver-only user attempted to mark job as GOA'); // Removed log
    setNotification({ open: true, message: 'Drivers cannot mark jobs as GOA.', severity: 'warning' }); // Use Snackbar
    setGoaDialogOpen(false);
    setJobToMarkGOA(null);
    return;
  }

  // Store the job ID in a local variable
  const jobId = jobToMarkGOA.id;
  
  const stopLoading = startLoading(); // Use startLoading
  
  try {
    // Use the jobService to mark the job as GOA
    await jobService.markJobAsGOA(jobId, goaReason);
    
    // Close dialog and refresh jobs
    setGoaDialogOpen(false);
    setJobToMarkGOA(null);
    refreshJobs();
    setNotification({ open: true, message: 'GOA request submitted successfully.', severity: 'success' }); // Use Snackbar
  } catch (error) {
    // console.error('Error marking job as GOA:', error); // Removed log
    setNotification({ open: true, message: 'Failed to mark job as GOA. Please try again.', severity: 'error' }); // Use Snackbar
  } finally {
    stopLoading(); // Use stopLoading
  }
};

// Handle Report Unsuccessful
export const handleReportUnsuccessful = async (jobToMarkUnsuccessful, unsuccessfulReason, startLoading, refreshJobs, setUnsuccessfulDialogOpen, setJobToMarkUnsuccessful, currentUser, setNotification) => {
  if (!jobToMarkUnsuccessful) {
    // console.error('No job selected for marking as unsuccessful'); // Removed log
    setNotification({ open: true, message: 'Error: No job selected for marking as unsuccessful', severity: 'error' });
    setUnsuccessfulDialogOpen(false);
    return;
  }

  // Check if the user is a driver-only user
  if (isDriverOnlyUser(currentUser)) {
    // console.log('Driver-only user attempted to mark job as unsuccessful'); // Removed log
    setNotification({ open: true, message: 'Drivers cannot report jobs as unsuccessful.', severity: 'warning' }); // Use Snackbar
    setUnsuccessfulDialogOpen(false);
    setJobToMarkUnsuccessful(null);
    return;
  }

  // Store the job ID in a local variable
  const jobId = jobToMarkUnsuccessful.id;
  
  const stopLoading = startLoading(); // Use startLoading
  
  try {
    // Use the jobService to mark the job as unsuccessful (now requires approval)
    await jobService.reportJobUnsuccessful(jobId, unsuccessfulReason);
    
    // Close dialog and refresh jobs
    setUnsuccessfulDialogOpen(false);
    setJobToMarkUnsuccessful(null);
    refreshJobs();
    
    // Show a message to the user that the request is pending approval
    setNotification({ open: true, message: 'Unsuccessful request submitted and awaiting approval.', severity: 'info' }); // Use Snackbar
    
  } catch (error) {
    // console.error('Error marking job as unsuccessful:', error); // Removed log
    setNotification({ open: true, message: 'Failed to mark job as unsuccessful. Please try again.', severity: 'error' }); // Use Snackbar
  } finally {
    stopLoading(); // Use stopLoading
  }
};

// Handle Approve Unsuccessful
export const handleApproveUnsuccessful = async (jobId, startLoading, refreshJobs, setNotification) => {
  const stopLoading = startLoading(); // Use startLoading
  
  try {
    // Use the jobService to approve the unsuccessful request
    await jobService.approveUnsuccessfulJob(jobId);
    
    // Refresh jobs to show the updated status
    refreshJobs();
    
    // Show a success message
    setNotification({ open: true, message: 'Unsuccessful request approved successfully.', severity: 'success' }); // Use Snackbar
    
  } catch (error) {
    // console.error('Error approving unsuccessful request:', error); // Removed log
    setNotification({ open: true, message: `Error approving unsuccessful request: ${error.message}`, severity: 'error' }); // Use Snackbar
  } finally {
    stopLoading(); // Use stopLoading
  }
};

// Handle Deny Unsuccessful
export const handleDenyUnsuccessful = async (jobId, startLoading, refreshJobs, setNotification) => {
  const stopLoading = startLoading(); // Use startLoading
  
  try {
    // Use the jobService to deny the unsuccessful request
    await jobService.denyUnsuccessfulJob(jobId);
    
    // Refresh jobs to show the updated status
    refreshJobs();
    
    // Show a success message
    setNotification({ open: true, message: 'Unsuccessful request denied. Job has been marked as canceled.', severity: 'info' }); // Use Snackbar
    
  } catch (error) {
    // console.error('Error denying unsuccessful request:', error); // Removed log
    setNotification({ open: true, message: `Error denying unsuccessful request: ${error.message}`, severity: 'error' }); // Use Snackbar
  } finally {
    stopLoading(); // Use stopLoading
  }
};

// Handle Approve GOA
export const handleApproveGOA = async (jobId, startLoading, refreshJobs, setNotification) => {
  const stopLoading = startLoading(); // Use startLoading
  
  try {
    // Use the jobService to approve the GOA request
    await jobService.approveGOA(jobId);
    
    // Refresh jobs to show the updated status
    refreshJobs();
    
    // Show a success message
    setNotification({ open: true, message: 'GOA request approved successfully.', severity: 'success' }); // Use Snackbar
    
  } catch (error) {
    // console.error('Error approving GOA request:', error); // Removed log
    setNotification({ open: true, message: `Error approving GOA request: ${error.message}`, severity: 'error' }); // Use Snackbar
  } finally {
    stopLoading(); // Use stopLoading
  }
};

// Handle Deny GOA
export const handleDenyGOA = async (jobId, startLoading, refreshJobs, setNotification) => {
  const stopLoading = startLoading(); // Use startLoading
  
  try {
    // Use the jobService to deny the GOA request
    await jobService.denyGOA(jobId);
    
    // Refresh jobs to show the updated status
    refreshJobs();
    
    // Show a success message
    setNotification({ open: true, message: 'GOA request denied successfully.', severity: 'info' }); // Use Snackbar
    
  } catch (error) {
    // console.error('Error denying GOA request:', error); // Removed log
    setNotification({ open: true, message: `Error denying GOA request: ${error.message}`, severity: 'error' }); // Use Snackbar
  } finally {
    stopLoading(); // Use stopLoading
  }
};

// Handle ETA update
export const handleEtaUpdateSave = async (additionalEtaValue, jobToUpdateEta, startLoading, refreshJobs, setEtaUpdateDialogOpen, setJobToUpdateEta, currentUser, setNotification) => {
  if (!jobToUpdateEta) {
    // console.error('No job selected for ETA update'); // Removed log
    setNotification({ open: true, message: 'Error: No job selected for ETA update', severity: 'error' });
    setEtaUpdateDialogOpen(false);
    return;
  }

  // Check if the user is a driver-only user
  if (isDriverOnlyUser(currentUser)) {
    // console.log('Driver-only user attempted to update ETA'); // Removed log
    setNotification({ open: true, message: 'Drivers cannot update ETAs on jobs.', severity: 'warning' }); // Use Snackbar
    setEtaUpdateDialogOpen(false);
    setJobToUpdateEta(null);
    return;
  }

  // Store the job ID in a local variable
  const jobId = jobToUpdateEta.id;
  
  // Get the current ETA value (as a number)
  const currentEta = parseInt(jobToUpdateEta.eta) || 0;
  
  // Calculate the new ETA by adding the additional time
  const newEta = currentEta + parseInt(additionalEtaValue);
  
  // console.log(`Updating ETA for job ${jobId}: ${currentEta} + ${additionalEtaValue} = ${newEta} minutes`); // Removed log
  
  const stopLoading = startLoading(); // Use startLoading
  
  try {
    // Use the dedicated ETA update endpoint to update only the ETA
    // This won't trigger re-acceptance or change any other job details
    await jobService.updateJobETA(jobId, newEta.toString());
    
    // Close dialog and refresh jobs
    setEtaUpdateDialogOpen(false);
    setJobToUpdateEta(null);
    refreshJobs();
    setNotification({ open: true, message: 'ETA updated successfully.', severity: 'success' }); // Use Snackbar
  } catch (error) {
    // console.error('Error updating job ETA:', error); // Removed log
    setNotification({ open: true, message: 'Failed to update job ETA. Please try again.', severity: 'error' }); // Use Snackbar
  } finally {
    stopLoading(); // Use stopLoading
  }
};

// NEW: Handle Cancel SP Assignment
// Added setJobs parameter for immediate state update
export const handleCancelSPAssignment = async (jobId, startLoading, refreshJobs, setJobs, setNotification) => {
  const stopLoading = startLoading();
  // console.log(`Attempting to cancel SP assignment for job ${jobId}`); // Removed log
  try {
    // Call the new backend endpoint
    const response = await jobService.cancelSPAssignment(jobId); 
    // console.log('SP assignment cancelled response:', response); // Removed log
    
    // Update local state immediately with the returned job data
    if (response && response.job) {
        const updatedJobData = response.job;

        // --- Process updated job data similar to fetchJobs ---
        let createdDisplay;
        if (updatedJobData.createdAt && !isNaN(new Date(updatedJobData.createdAt).getTime())) {
            const createdDate = new Date(updatedJobData.createdAt);
            const today = new Date();
            const isCreatedToday = 
              createdDate.getFullYear() === today.getFullYear() &&
              createdDate.getMonth() === today.getMonth() &&
              createdDate.getDate() === today.getDate();
            if (isCreatedToday) {
              createdDisplay = createdDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
            } else {
              createdDisplay = createdDate.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
            }
        } else {
            createdDisplay = updatedJobData.created || 'Unknown';
        }

        const processedUpdatedJob = {
            ...updatedJobData,
            id: updatedJobData._id.toString(),
            created: createdDisplay, // Apply formatted date/time
            // Ensure SP specific fields are explicitly cleared in the frontend state object
            // even though backend clears them, this guarantees UI update
            assignedVendorNumber: updatedJobData.assignedVendorNumber || null, 
            assignedSPCompanyName: updatedJobData.assignedSPCompanyName || null, 
            expanded: false // Collapse after action
        };
        // --- End processing ---
        
        setJobs(prevJobs => {
            // console.log(`Updating local state for job ${processedUpdatedJob.id}`); // Removed log
            return prevJobs.map(job => 
                job.id === processedUpdatedJob.id ? processedUpdatedJob : job
            );
        });
    } else {
        // Fallback to refresh if response format is unexpected
        // console.warn('No updated job data in response, falling back to refreshJobs()'); // Removed log
        refreshJobs();
    }

    setNotification({ open: true, message: response.message || 'SP assignment cancelled successfully. Searching for next candidate.', severity: 'success' }); // Use Snackbar
    // refreshJobs(); // Refresh might be redundant now, but keep for safety? Or remove? Let's remove for now.
  } catch (error) {
    // console.error('Error cancelling SP assignment:', error); // Removed log
    // Refresh jobs even on error to try and get consistent state
    refreshJobs(); 
    setNotification({ open: true, message: `Failed to cancel SP assignment: ${error.response?.data?.message || error.message}`, severity: 'error' }); // Use Snackbar
  } finally {
    stopLoading();
  }
};

// NEW: Handle Find Next Org 1 Driver
export const handleFindNextOrg1Driver = async (jobId, startLoading, refreshJobs, setJobs, setNotification) => {
  const stopLoading = startLoading();
  // console.log(`Attempting to find next Org 1 driver for job ${jobId}`); // Removed log
  try {
    // Call the new backend endpoint (to be created)
    const response = await jobService.findNextOrg1Driver(jobId);
    // console.log('Find next Org 1 driver response:', response); // Removed log

    // Update local state immediately with the returned job data if successful
    if (response && response.job) {
      const updatedJobData = response.job;

      // --- Process updated job data similar to fetchJobs ---
      let createdDisplay;
      if (updatedJobData.createdAt && !isNaN(new Date(updatedJobData.createdAt).getTime())) {
          const createdDate = new Date(updatedJobData.createdAt);
          const today = new Date();
          const isCreatedToday =
            createdDate.getFullYear() === today.getFullYear() &&
            createdDate.getMonth() === today.getMonth() &&
            createdDate.getDate() === today.getDate();
          if (isCreatedToday) {
            createdDisplay = createdDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
          } else {
            createdDisplay = createdDate.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
          }
      } else {
          createdDisplay = updatedJobData.created || 'Unknown';
      }

      const processedUpdatedJob = {
          ...updatedJobData,
          id: updatedJobData._id.toString(),
          created: createdDisplay, // Apply formatted date/time
          expanded: false // Collapse after action
      };
      // --- End processing ---

      setJobs(prevJobs => {
          // console.log(`Updating local state for job ${processedUpdatedJob.id} after finding next driver`); // Removed log
          return prevJobs.map(job =>
              job.id === processedUpdatedJob.id ? processedUpdatedJob : job
          );
      });
      setNotification({ open: true, message: response.message || 'Found and assigned next available Org 1 driver.', severity: 'success' }); // Use Snackbar
    } else if (response && response.message) {
        // Handle cases where no driver was found but a message is returned
        setNotification({ open: true, message: response.message, severity: 'info' }); // Use Snackbar
        // Optionally refresh jobs to ensure consistency if status changed to Pending
        refreshJobs();
    } else {
      // Fallback to refresh if response format is unexpected
      // console.warn('Unexpected response format from findNextOrg1Driver, falling back to refreshJobs()'); // Removed log
      refreshJobs();
    }

  } catch (error) {
    // console.error('Error finding next Org 1 driver:', error); // Removed log
    // Refresh jobs even on error to try and get consistent state
    refreshJobs();
    setNotification({ open: true, message: `Failed to find next Org 1 driver: ${error.response?.data?.message || error.message}`, severity: 'error' }); // Use Snackbar
  } finally {
    stopLoading();
  }
};

// NEW: Handle Toggle Automated Dispatch
export const handleToggleAutomatedDispatch = async (jobId, startLoading, refreshJobs, setJobs, setNotification) => {
  const stopLoading = startLoading();
  // console.log(`Attempting to toggle automated dispatch for job ${jobId}`); // Removed log
  try {
    const response = await jobService.toggleAutomatedDispatch(jobId);
    // console.log('Toggle automated dispatch response:', response); // Removed log

    // Update local state immediately
    if (response && response.job) {
      const updatedJobData = response.job;
      // Process job data (similar to other handlers)
      let createdDisplay;
      if (updatedJobData.createdAt && !isNaN(new Date(updatedJobData.createdAt).getTime())) {
          const createdDate = new Date(updatedJobData.createdAt);
          const today = new Date();
          const isCreatedToday =
            createdDate.getFullYear() === today.getFullYear() &&
            createdDate.getMonth() === today.getMonth() &&
            createdDate.getDate() === today.getDate();
          if (isCreatedToday) {
            createdDisplay = createdDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
          } else {
            createdDisplay = createdDate.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
          }
      } else {
          createdDisplay = updatedJobData.created || 'Unknown';
      }
      const processedUpdatedJob = {
          ...updatedJobData,
          id: updatedJobData._id.toString(),
          created: createdDisplay,
          expanded: false // Keep collapsed or preserve? Let's collapse.
      };
      setJobs(prevJobs => prevJobs.map(job => job.id === processedUpdatedJob.id ? processedUpdatedJob : job));
      // Use Snackbar notification instead of alert
      if (setNotification) { // Check if setNotification is provided
        setNotification({ open: true, message: response.message || 'Automated dispatch toggled successfully.', severity: 'success' });
      } else {
        console.log(response.message || 'Automated dispatch toggled successfully.'); // Fallback to console log if no setNotification
      }
    } else {
      refreshJobs(); // Fallback refresh
    }
  } catch (error) {
    // console.error('Error toggling automated dispatch:', error); // Removed log
    refreshJobs(); // Refresh on error
    // Use Snackbar notification for error
    if (setNotification) { // Check if setNotification is provided
      setNotification({ open: true, message: `Failed to toggle automated dispatch: ${error.response?.data?.message || error.message}`, severity: 'error' });
    } else {
      console.error(`Failed to toggle automated dispatch: ${error.response?.data?.message || error.message}`); // Fallback to console error
    }
  } finally {
    stopLoading();
  }
};


// Handle menu actions
export const handleMenuAction = async (
  action, 
  job, 
  setJobToCancel,
  setCancelDialogOpen,
  setJobToMarkGOA,
  setGoaDialogOpen,
  setJobToUpdateEta,
  setEtaUpdateDialogOpen,
  setJobToMarkUnsuccessful,
  setUnsuccessfulDialogOpen,
  handleDispatchJob,
  refreshJobs,
  startLoading, // Changed setLoading to startLoading
  currentUser,
  setNotification
) => {
  // const { startLoading } = useLoading(); // Removed - Hook cannot be called here
  // If job is provided directly (e.g., from the Rejected status button), use it
  // Otherwise use the selectedJobForMenu from the menu context
  const targetJob = job;
  
  if (!targetJob) {
    // console.error('No job selected for action:', action); // Removed log
    return;
  }
  
  // Check if the user is a driver-only user
  const isDriverOnly = isDriverOnlyUser(currentUser);
  
  // Restricted actions for driver-only users
  const driverRestrictedActions = ['goa', 'updateEta', 'unsuccessful', 'reassign'];
  
  if (isDriverOnly && driverRestrictedActions.includes(action)) {
    // console.log(`Driver-only user attempted restricted action: ${action}`); // Removed log
    setNotification({ open: true, message: `Drivers cannot perform this action: ${action}`, severity: 'warning' }); // Use Snackbar
    return;
  }
  
  // Additional check for completed/canceled jobs for driver-only users
  if (isDriverOnly && action === 'cancel' && 
      (targetJob.status === 'Completed' || targetJob.status === 'Canceled' || 
       targetJob.status === 'GOA' || targetJob.status === 'Unsuccessful')) {
    setNotification({ open: true, message: 'Drivers cannot cancel completed or canceled jobs.', severity: 'warning' }); // Use Snackbar
    return;
  }
  
  switch (action) {
    case 'approveETA':
      // Handle approving ETA
      try {
        const stopLoading = startLoading();
        try {
          await jobService.approveETA(targetJob.id);
          refreshJobs();
          setNotification({ open: true, message: 'ETA approved successfully', severity: 'success' });
        } catch (error) {
          console.error('Error approving ETA:', error);
          setNotification({ open: true, message: 'Failed to approve ETA. Please try again.', severity: 'error' });
        } finally {
          stopLoading();
        }
      } catch (error) {
        console.error('Error in approveETA handler:', error);
      }
      break;
    case 'denyETA':
      // Handle denying ETA
      try {
        const stopLoading = startLoading();
        try {
          await jobService.denyETA(targetJob.id, 'ETA rejected by user');
          refreshJobs();
          setNotification({ open: true, message: 'ETA denied successfully', severity: 'success' });
        } catch (error) {
          console.error('Error denying ETA:', error);
          setNotification({ open: true, message: 'Failed to deny ETA. Please try again.', severity: 'error' });
        } finally {
          stopLoading();
        }
      } catch (error) {
        console.error('Error in denyETA handler:', error);
      }
      break;
    case 'approveUnsuccessful':
      // Handle approving unsuccessful request
      await handleApproveUnsuccessful(targetJob.id, startLoading, refreshJobs, setNotification);
      break;
    case 'denyUnsuccessful':
      // Handle denying unsuccessful request
      await handleDenyUnsuccessful(targetJob.id, startLoading, refreshJobs, setNotification);
      break;
    case 'approveGOA':
      // Handle approving GOA request
      await handleApproveGOA(targetJob.id, startLoading, refreshJobs, setNotification);
      break;
    case 'denyGOA':
      // Handle denying GOA request
      await handleDenyGOA(targetJob.id, startLoading, refreshJobs, setNotification);
      break;
    case 'cancel':
      // Only allow canceling if the job is NOT completed
      if (targetJob.status !== 'Completed') {
        // Store the job in a dedicated state variable for cancellation
        setJobToCancel(targetJob);
        setCancelDialogOpen(true);
      } else {
        setNotification({ open: true, message: 'Completed jobs cannot be canceled.', severity: 'warning' }); // Use Snackbar
      }
      break;
    case 'goa':
      // Store the job in a dedicated state variable for GOA
      setJobToMarkGOA(targetJob);
      setGoaDialogOpen(true);
      break;
    case 'updateEta':
      // Store the job in a dedicated state variable for ETA update
      setJobToUpdateEta(targetJob);
      setEtaUpdateDialogOpen(true);
      break;
    case 'unsuccessful':
      // Store the job in a dedicated state variable for marking as unsuccessful
      setJobToMarkUnsuccessful(targetJob);
      setUnsuccessfulDialogOpen(true);
      break;
    case 'email':
      // Implement email job details logic
      setNotification({ open: true, message: 'Email Job Details feature coming soon', severity: 'info' }); // Use Snackbar
      break;
    case 'duplicate':
      try {
        await jobService.duplicateJob(targetJob.id);
        // Refresh the jobs list to show the new duplicated job
        refreshJobs();
        setNotification({ open: true, message: 'Job duplicated successfully!', severity: 'success' }); // Use Snackbar
      } catch (error) {
        // console.error('Error duplicating job:', error); // Removed log
        setNotification({ open: true, message: 'Failed to duplicate job. Please try again.', severity: 'error' }); // Use Snackbar
      }
      break;
    case 'reassign':
      // Use the existing dispatch dialog to reassign the driver
      handleDispatchJob(targetJob);
      break;
    case 'unassignFromSP':
      // Handle unassigning job from Service Provider
      try {
        const stopLoading = startLoading();
        try {
          await jobService.unassignFromServiceProvider(targetJob.id);
          refreshJobs();
          setNotification({ open: true, message: 'Job unassigned from Service Provider successfully', severity: 'success' });
        } catch (error) {
          console.error('Error unassigning job from Service Provider:', error);
          setNotification({ open: true, message: 'Failed to unassign job from Service Provider. Please try again.', severity: 'error' });
        } finally {
          stopLoading();
        }
      } catch (error) {
        console.error('Error in unassignFromSP handler:', error);
      }
      break;
    case 'unassignDriver':
      // Handle unassigning only the driver (for Org 2 users)
      try {
        const stopLoading = startLoading();
        try {
          await jobService.unassignDriverOnly(targetJob.id);
          refreshJobs();
          setNotification({ open: true, message: 'Driver unassigned successfully', severity: 'success' });
        } catch (error) {
          console.error('Error unassigning driver:', error);
          setNotification({ open: true, message: 'Failed to unassign driver. Please try again.', severity: 'error' });
        } finally {
          stopLoading();
        }
      } catch (error) {
        console.error('Error in unassignDriver handler:', error);
      }
      break;
    default:
      break;
  }
};

// NEW: Handle Approve Org 1 Driver ETA
export const handleApproveOrg1ETA = async (job, startLoading, refreshJobs, setNotification) => {
  if (!job) {
    console.error('No job provided for approving Org 1 ETA');
    setNotification({ open: true, message: 'Error: Cannot approve ETA without job details.', severity: 'error' });
    return;
  }
  const stopLoading = startLoading();
  try {
    await jobService.approveOrg1DriverETA(job.id);
    refreshJobs();
    setNotification({ open: true, message: 'Org 1 Driver ETA approved successfully.', severity: 'success' });
  } catch (error) {
    console.error('Error approving Org 1 Driver ETA:', error);
    setNotification({ open: true, message: `Failed to approve Org 1 Driver ETA: ${error.response?.data?.message || error.message}`, severity: 'error' });
  } finally {
    stopLoading();
  }
};

// NEW: Handle Deny Org 1 Driver ETA
export const handleDenyOrg1ETA = async (job, startLoading, refreshJobs, setNotification) => {
  if (!job) {
    console.error('No job provided for denying Org 1 ETA');
    setNotification({ open: true, message: 'Error: Cannot deny ETA without job details.', severity: 'error' });
    return;
  }
  const stopLoading = startLoading();
  try {
    await jobService.rejectOrg1DriverETA(job.id);
    refreshJobs();
    setNotification({ open: true, message: 'Org 1 Driver ETA rejected successfully.', severity: 'info' });
  } catch (error) {
    console.error('Error rejecting Org 1 Driver ETA:', error);
    setNotification({ open: true, message: `Failed to reject Org 1 Driver ETA: ${error.response?.data?.message || error.message}`, severity: 'error' });
  } finally {
    stopLoading();
  }
};
